import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { type DecisionNodeData } from '@app/services/aiFlow.types';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';

type DecisionFormProps = {
    readonly id: string;
    readonly data: DecisionNodeData;
    readonly number: string | null;
    readonly onSubmit: (data: DecisionNodeData) => void;
    readonly onCancel: () => void;
};

const schema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
    decisionVariable: z.string().trim().min(1, 'Assessment variable is required'),
    passed: z.string().trim().min(1, 'Passed action is required'),
    failed: z.string().trim().min(1, 'Failed action is required'),
    visConditionSlots: z.coerce.number().min(0).max(3),
});

function DecisionForm({ id, data, number, onSubmit, onCancel }: DecisionFormProps) {
    const {
        register,
        handleSubmit,
        reset,
        formState: { isValid, isDirty },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: data,
    });

    useEffect(() => {
        reset(data);
    }, [data, reset]);

    return (
        <>
            <div className="order-tag">
                <span>{number ?? '--'}</span>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} key={id}>
                <div>
                    <label htmlFor="name-input">
                        Name
                        <textarea {...register('name')} id="name-input" autoFocus />
                    </label>
                </div>
                <div>
                    <label htmlFor="decisionVariable-input">
                        Assess
                        <textarea {...register('decisionVariable')} id="decisionVariable-input" />
                    </label>
                </div>
                <div>
                    <label htmlFor="passed-input">
                        Passed Action
                        <textarea {...register('passed')} id="passed-input" />
                    </label>
                </div>
                <div>
                    <label htmlFor="failed-input">
                        Failed Action
                        <textarea {...register('failed')} id="failed-input" />
                    </label>
                </div>
                <StyledButtonGroup className="margin-top-10">
                    <StyledButton htmlType="submit" disabled={!isValid || !isDirty}>
                        Save
                    </StyledButton>
                    <StyledButton htmlType="button" onClick={onCancel}>
                        Cancel
                    </StyledButton>
                </StyledButtonGroup>
            </form>
        </>
    );
}

export default DecisionForm;
